/**
 * The conversation on a ticket.
 *
 * Three treatments, each of which means something rather than just being a
 * different colour. An internal note is a `crm-note` - the same surface a call
 * note gets, because it is the same thing: something a person wrote that the
 * client will never see. A bot message is a `crm-ai-box`, which is the app's one
 * signal for "a machine wrote this". Everything else - staff replying, the
 * client writing in - is a plain row, told apart by the avatar and the name, not
 * by a fourth background.
 *
 * The old thread painted all three plus a primary tint on staff replies, so
 * four of the five rows on a busy ticket were coloured and none of the colours
 * carried a fact.
 *
 * Files hang under the text as chips, the same ones the mailbox draws. A chip
 * with nothing behind it is still drawn, as plain text: a message that carried
 * `leping.pdf` before the desk kept bytes should say so, because an operator who
 * can read the name knows what to ask the client for. Hiding it would turn a
 * recoverable gap into a message that appears never to have carried anything.
 */
import { Lock, Sparkles, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { ticketAttachmentUrl, type StaffMember, type TicketMessage } from "../../../lib/support";
import { Avatar, fmtDateTime } from "../../ui";
import { AttachmentChips } from "../_shared/attachments";
import { staffName } from "./tone";

export function TicketThread({
  messages,
  staff,
  onDelete,
}: {
  messages: TicketMessage[];
  staff: StaffMember[];
  onDelete: (m: TicketMessage) => void;
}) {
  const t = useT();

  if (messages.length === 0) {
    return <p className="crm-mut text-[13px]">{t("support.noMessages")}</p>;
  }

  return (
    <ul className="flex flex-col gap-3">
      {messages.map((m) => (
        <li key={m.id}>
          <Message m={m} staff={staff} onDelete={() => onDelete(m)} />
        </li>
      ))}
    </ul>
  );
}

function Message({
  m,
  staff,
  onDelete,
}: {
  m: TicketMessage;
  staff: StaffMember[];
  onDelete: () => void;
}) {
  const t = useT();
  const isStaff = m.authorType === "staff";
  const author = isStaff ? staffName(staff, m.author) : m.author;

  // An internal note and a bot reply are their own surface; a plain message
  // takes no box at all, which is what keeps a long thread readable.
  const boxed = m.internal || m.authorType === "bot";

  return (
    <div className={cn(m.internal && "crm-note !mb-0", m.authorType === "bot" && !m.internal && "crm-ai-box")}>
      <div className="flex items-start gap-2.5">
        {boxed ? null : <Avatar name={author} />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="crm-prow-n">{author}</span>
            <span
              className={cn(
                "inline-flex items-center gap-1 text-[11.5px] font-semibold",
                m.internal ? "text-[var(--color-warning)]" : "crm-mut",
                m.authorType === "bot" && !m.internal && "text-[var(--color-primary)]",
              )}
            >
              {m.internal ? <Lock className="size-3" /> : null}
              {m.authorType === "bot" && !m.internal ? <Sparkles className="size-3" /> : null}
              {m.internal ? t("support.internalTag") : t(`support.authorType.${m.authorType}`)}
            </span>
            <span className="crm-prow-s crm-tnum crm-push">{fmtDateTime(m.createdAt)}</span>
            <button
              type="button"
              className="crm-cb crm-cb-sm"
              onClick={onDelete}
              title={t("common.delete")}
              aria-label={t("common.delete")}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
          <p className="mt-1 whitespace-pre-wrap text-[13.25px] leading-relaxed">{m.body}</p>
          <AttachmentChips
            t={t}
            items={m.attachments ?? []}
            // `null` for a record with no bytes, which is what turns the chip
            // from a link into a label. The URL carries an index, never a
            // storage key - see `ticketAttachmentUrl`.
            href={(i, a) => (a.stored ? ticketAttachmentUrl(m.ticketId, m.id, i) : null)}
          />
        </div>
      </div>
    </div>
  );
}
